import naja from 'naja';

declare global {
    interface Window {
        dataLayer: any[];
    }
}

class GtmTracking {
    private gtmDataAttribute = 'data-gtm-view-item-list';
    private gtmViewItemAttribute = 'data-gtm-view-item';
    private gtmAddToCartAttribute = 'data-gtm-add-to-cart';
    private gtmRemoveFromCartAttribute = 'data-gtm-remove-from-cart';
    private gtmBeginCheckoutAttribute = 'data-gtm-begin-checkout';
    private gtmAddShippingInfoAttribute = 'data-gtm-add-shipping-info';
    private gtmAddPaymentInfoAttribute = 'data-gtm-add-payment-info';
    private gtmGenerateLeadAttribute = 'data-gtm-generate-lead';
    private observer?: IntersectionObserver;
    private selectItemListenerAdded = false;

    public init(): void {
        this.setupViewItemListTracking();
        this.setupViewItemTracking();
        this.setupAddToCartTracking();
        this.setupRemoveFromCartTracking();
        this.setupBeginCheckoutTracking();
        this.setupAddShippingInfoTracking();
        this.setupAddPaymentInfoTracking();
        this.setupGenerateLeadTracking();
        this.setupSelectItemTracking();
        this.bindAjaxEvents();
    }

    private setupViewItemListTracking(): void {
        // Find all elements with GTM data
        const elementsWithGtmData = document.querySelectorAll(`[${this.gtmDataAttribute}]`);
        
        elementsWithGtmData.forEach(element => {
            this.trackViewItemList(element as HTMLElement);
        });
    }

    private trackViewItemList(element: HTMLElement): void {
        // Check if this element has already been processed
        if (element.getAttribute('data-gtm-processed') === 'true') {
            return;
        }

        const gtmDataStr = element.getAttribute(this.gtmDataAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendViewItemListEvent(gtmData);
            
            // Mark element as processed to prevent duplicate events
            element.setAttribute('data-gtm-processed', 'true');
        } catch (error) {
            console.error('Error parsing GTM data:', error);
        }
    }

    private sendViewItemListEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the view_item_list event
        window.dataLayer.push(data);
        
        console.log('GTM view_item_list event sent:', data);
    }

    private setupViewItemTracking(): void {
        // Find all elements with view_item GTM data
        const elementsWithViewItemData = document.querySelectorAll(`[${this.gtmViewItemAttribute}]`);
        
        elementsWithViewItemData.forEach(element => {
            this.trackViewItem(element as HTMLElement);
        });
    }

    private trackViewItem(element: HTMLElement): void {
        // Check if this element has already been processed
        if (element.getAttribute('data-gtm-view-item-processed') === 'true') {
            return;
        }

        const gtmDataStr = element.getAttribute(this.gtmViewItemAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendViewItemEvent(gtmData);
            
            // Mark element as processed to prevent duplicate events
            element.setAttribute('data-gtm-view-item-processed', 'true');
        } catch (error) {
            console.error('Error parsing view_item GTM data:', error);
        }
    }

    private sendViewItemEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the view_item event
        window.dataLayer.push(data);
        
        console.log('GTM view_item event sent:', data);
    }

    private setupAddToCartTracking(): void {
        // Find all elements with add_to_cart GTM data
        const elementsWithAddToCartData = document.querySelectorAll(`[${this.gtmAddToCartAttribute}]`);
        
        elementsWithAddToCartData.forEach(element => {
            this.trackAddToCart(element as HTMLElement);
        });
    }

    private trackAddToCart(element: HTMLElement): void {
        const gtmDataStr = element.getAttribute(this.gtmAddToCartAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendAddToCartEvent(gtmData);
            
            // Remove the element after sending the event so it doesn't fire again
            element.remove();
        } catch (error) {
            console.error('Error parsing add_to_cart GTM data:', error);
        }
    }

    private sendAddToCartEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the add_to_cart event
        window.dataLayer.push(data);
        
        console.log('GTM add_to_cart event sent:', data);
    }

    private setupRemoveFromCartTracking(): void {
        // Find all elements with remove_from_cart GTM data
        const elementsWithRemoveFromCartData = document.querySelectorAll(`[${this.gtmRemoveFromCartAttribute}]`);
        
        elementsWithRemoveFromCartData.forEach(element => {
            this.trackRemoveFromCart(element as HTMLElement);
        });
    }

    private trackRemoveFromCart(element: HTMLElement): void {
        const gtmDataStr = element.getAttribute(this.gtmRemoveFromCartAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendRemoveFromCartEvent(gtmData);
            
            // Remove the element after sending the event so it doesn't fire again
            element.remove();
        } catch (error) {
            console.error('Error parsing remove_from_cart GTM data:', error);
        }
    }

    private sendRemoveFromCartEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the remove_from_cart event
        window.dataLayer.push(data);
        
        console.log('GTM remove_from_cart event sent:', data);
    }

    private setupBeginCheckoutTracking(): void {
        // Find all elements with begin_checkout GTM data
        const elementsWithBeginCheckoutData = document.querySelectorAll(`[${this.gtmBeginCheckoutAttribute}]`);
        
        elementsWithBeginCheckoutData.forEach(element => {
            this.trackBeginCheckout(element as HTMLElement);
        });
    }

    private trackBeginCheckout(element: HTMLElement): void {
        const gtmDataStr = element.getAttribute(this.gtmBeginCheckoutAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendBeginCheckoutEvent(gtmData);
            
            // Remove the element after sending the event so it doesn't fire again
            element.remove();
        } catch (error) {
            console.error('Error parsing begin_checkout GTM data:', error);
        }
    }

    private sendBeginCheckoutEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the begin_checkout event
        window.dataLayer.push(data);
        
        console.log('GTM begin_checkout event sent:', data);
    }

    private setupAddShippingInfoTracking(): void {
        // Find all elements with add_shipping_info GTM data
        const elementsWithAddShippingInfoData = document.querySelectorAll(`[${this.gtmAddShippingInfoAttribute}]`);
        
        elementsWithAddShippingInfoData.forEach(element => {
            this.trackAddShippingInfo(element as HTMLElement);
        });
    }

    private trackAddShippingInfo(element: HTMLElement): void {
        const gtmDataStr = element.getAttribute(this.gtmAddShippingInfoAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendAddShippingInfoEvent(gtmData);
            
            // Remove the element after sending the event so it doesn't fire again
            element.remove();
        } catch (error) {
            console.error('Error parsing add_shipping_info GTM data:', error);
        }
    }

    private sendAddShippingInfoEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the add_shipping_info event
        window.dataLayer.push(data);
        
        console.log('GTM add_shipping_info event sent:', data);
    }

    private setupAddPaymentInfoTracking(): void {
        // Find all elements with add_payment_info GTM data
        const elementsWithAddPaymentInfoData = document.querySelectorAll(`[${this.gtmAddPaymentInfoAttribute}]`);
        
        elementsWithAddPaymentInfoData.forEach(element => {
            this.trackAddPaymentInfo(element as HTMLElement);
        });
    }

    private trackAddPaymentInfo(element: HTMLElement): void {
        const gtmDataStr = element.getAttribute(this.gtmAddPaymentInfoAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendAddPaymentInfoEvent(gtmData);
            
            // Remove the element after sending the event so it doesn't fire again
            element.remove();
        } catch (error) {
            console.error('Error parsing add_payment_info GTM data:', error);
        }
    }

    private sendAddPaymentInfoEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the add_payment_info event
        window.dataLayer.push(data);
        
        console.log('GTM add_payment_info event sent:', data);
    }

    private setupGenerateLeadTracking(): void {
        // Find all elements with generate_lead GTM data
        const elementsWithGenerateLeadData = document.querySelectorAll(`[${this.gtmGenerateLeadAttribute}]`);
        
        elementsWithGenerateLeadData.forEach(element => {
            this.trackGenerateLead(element as HTMLElement);
        });
    }

    private trackGenerateLead(element: HTMLElement): void {
        const gtmDataStr = element.getAttribute(this.gtmGenerateLeadAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            this.sendGenerateLeadEvent(gtmData);
            
            // Remove the element after sending the event so it doesn't fire again
            element.remove();
        } catch (error) {
            console.error('Error parsing generate_lead GTM data:', error);
        }
    }

    private sendGenerateLeadEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the generate_lead event
        window.dataLayer.push(data);
        
        console.log('GTM generate_lead event sent:', data);
    }

    private setupSelectItemTracking(): void {
        // Only add the event listener once
        if (!this.selectItemListenerAdded) {
            document.addEventListener('click', (event) => {
                const target = event.target as HTMLElement;
                const productLink = target.closest('.category-item');
                
                if (productLink && productLink.tagName === 'A') {
                    this.trackSelectItem(productLink as HTMLAnchorElement);
                }
            });
            this.selectItemListenerAdded = true;
        }
    }

    private trackSelectItem(productLink: HTMLAnchorElement): void {
        // Find the parent container with view_item_list data
        const listContainer = productLink.closest(`[${this.gtmDataAttribute}]`) as HTMLElement;
        
        if (!listContainer) return;

        const gtmDataStr = listContainer.getAttribute(this.gtmDataAttribute);
        if (!gtmDataStr) return;

        try {
            const gtmData = JSON.parse(gtmDataStr);
            
            // Find the product index in the list
            const allProductLinks = listContainer.querySelectorAll('.category-item');
            let productIndex = -1;
            
            for (let i = 0; i < allProductLinks.length; i++) {
                if (allProductLinks[i] === productLink) {
                    productIndex = i;
                    break;
                }
            }

            if (productIndex >= 0 && gtmData.ecommerce && gtmData.ecommerce.items) {
                const selectedProduct = gtmData.ecommerce.items[productIndex];
                
                if (selectedProduct) {
                    this.sendSelectItemEvent({
                        event: 'select_item',
                        ecommerce: {
                            item_list_id: gtmData.ecommerce.item_list_id,
                            item_list_name: gtmData.ecommerce.item_list_name,
                            items: [selectedProduct]
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Error tracking select_item:', error);
        }
    }

    private sendSelectItemEvent(data: any): void {
        // Clear previous ecommerce data
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ ecommerce: null });
        
        // Push the select_item event
        window.dataLayer.push(data);
        
        console.log('GTM select_item event sent:', data);
    }

    private bindAjaxEvents(): void {
        // Listen for snippet redraws after AJAX requests
        naja.snippetHandler.addEventListener('afterUpdate', (event: { detail: { snippet: { id: string } } }) => {
            console.log('GTM: AJAX snippet updated:', event.detail.snippet.id);
            
            // Re-initialize tracking after product list updates
            if (event.detail.snippet.id === 'products' || 
                event.detail.snippet.id.includes('category') ||
                event.detail.snippet.id.includes('search')) {
                
                // Small delay to ensure DOM is updated
                setTimeout(() => {
                    this.setupViewItemListTracking();
                    // Don't re-setup select item tracking as it's a global listener
                }, 100);
            }
            
            // Check for cart events after AJAX notifications
            if (event.detail.snippet.id === 'snippet--ajaxNotification') {
                console.log('GTM: Processing ajaxNotification snippet update');
                // Increase delay and ensure DOM is ready
                setTimeout(() => {
                    console.log('GTM: Re-initializing cart tracking');
                    this.setupAddToCartTracking();
                    this.setupRemoveFromCartTracking();
                    this.setupBeginCheckoutTracking();
                    this.setupAddShippingInfoTracking();
                    this.setupAddPaymentInfoTracking();
                }, 150);
            }
        });
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    const gtmTracking = new GtmTracking();
    gtmTracking.init();
});

export default GtmTracking;